/**
 * Peticiones de propietarios — tipos y etiquetas.
 *
 * Los documentos los escribe la web (tusereno.es/propietarios/solicitud); aquí
 * solo se leen y se decide sobre ellos. Los códigos deben coincidir con
 * `lib/peticiones/model.ts` del repo de la web: si allí se añade una opción,
 * hay que añadirla aquí o se verá el código crudo en la ficha.
 */

export type EstadoPeticion =
  | 'nueva'
  | 'aceptada'
  | 'rechazada'
  | 'descartada'
  | 'pendiente_licencia';

export interface FotoPeticion {
  blobPath: string;
  size: number;
}

export interface Peticion {
  id: string;
  type: 'peticion';
  estado: EstadoPeticion;
  motivoDescarte?: 'zona' | 'piso_sin_licencia';
  esProyectoEspecial?: boolean;
  zona?: string;
  zonaOtroDetalle?: string;
  numPropiedades?: string;
  licencia?: string;
  tipoUnidad?: string;
  direccion?: string;
  descripcion?: string;
  proyectoDetalle?: string;
  fotos?: FotoPeticion[];
  expectativas?: string;
  canal?: string;
  canalOtroDetalle?: string;
  nombre?: string;
  email?: string;
  telefono?: string;
  leadId?: string;
  duplicadoDe?: { tipo: 'lead' | 'owner'; id: string };
  decision?: { por: string; at: string; nota?: string } | null;
  correo?: { estado: 'enviado' | 'error' | 'no_configurado'; detalle?: string | null; at: string };
  createdAt: string;
  updatedAt: string;
}

export const ESTADO_LABELS: Record<EstadoPeticion, string> = {
  nueva: 'Nueva',
  pendiente_licencia: 'Pendiente de licencia',
  aceptada: 'Aceptada',
  rechazada: 'Rechazada',
  descartada: 'Descartada',
};

export const ESTADO_BADGES: Record<EstadoPeticion, string> = {
  nueva: 'bg-blue-100 text-blue-700',
  pendiente_licencia: 'bg-amber-100 text-amber-700',
  aceptada: 'bg-green-100 text-green-700',
  rechazada: 'bg-red-100 text-red-700',
  descartada: 'bg-gray-100 text-gray-500',
};

export const ZONA_LABELS: Record<string, string> = {
  barbate: 'Barbate',
  zahara: 'Zahara de los Atunes',
  canos: 'Los Caños de Meca',
  otro: 'Otro lugar',
};

export const NUM_PROPIEDADES_LABELS: Record<string, string> = {
  '1': '1 propiedad',
  '2': '2 propiedades',
  '3': '3 propiedades',
  mas3: 'Más de 3 propiedades',
  proyecto: 'Hotel, hostal u otro proyecto',
};

export const LICENCIA_LABELS: Record<string, string> = {
  si: 'Con licencia',
  algunas: 'Solo algunas con licencia',
  no: 'Sin licencia',
};

export const TIPO_UNIDAD_LABELS: Record<string, string> = {
  piso: 'Piso',
  independiente: 'Unidad independiente',
  mixto: 'De ambos tipos',
};

export const EXPECTATIVAS_LABELS: Record<string, string> = {
  lt2000: 'Menos de 2.000 €/año',
  '2000_5000': '2.000–5.000 €/año',
  '5000_10000': '5.000–10.000 €/año',
  '10000_15000': '10.000–15.000 €/año',
  gt15000: 'Más de 15.000 €/año',
  nsnc: 'Aún no lo sabe',
};

export const CANAL_LABELS: Record<string, string> = {
  google: 'Google',
  conocido: 'Un conocido',
  cliente: 'Un cliente de Sereno',
  ia: 'Se lo sugirió una IA',
  otro: 'Otro',
};

export const MOTIVO_DESCARTE_LABELS: Record<string, string> = {
  zona: 'Fuera de las localidades donde trabajamos',
  piso_sin_licencia: 'Piso sin licencia (requiere acuerdo de la comunidad)',
};

/** Una etiqueta legible o el código crudo si la web añadió una opción nueva. */
export function etiqueta(mapa: Record<string, string>, codigo?: string): string | undefined {
  if (!codigo) return undefined;
  return mapa[codigo] ?? codigo;
}

/** Estados sobre los que todavía se puede decidir. */
export function esDecidible(estado: EstadoPeticion): boolean {
  return estado === 'nueva' || estado === 'pendiente_licencia' || estado === 'descartada';
}
